/**
 * BankPanel.jsx — the Bank tab under Company Accounting.
 *
 * Three states:
 *   not configured — Plaid keys missing from server/.env
 *   no connections — setup prompt
 *   connected      — runway strip + review queue / spend / accounts
 */
import { useState } from 'react';
import {
  Box, Paper, Typography, Alert, Button, ToggleButtonGroup, ToggleButton,
  Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Switch, Tooltip, CircularProgress,
} from '@mui/material';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import LinkOffRoundedIcon from '@mui/icons-material/LinkOffRounded';
import { fmtC } from '../../lib/utils';
import { useToast } from '../layout/Toast';
import ConnectBankButton from './ConnectBankButton';
import RunwayStrip from './RunwayStrip';
import ReviewQueue from './ReviewQueue';
import SpendByCategory from './SpendByCategory';
import {
  useBankStatus, useBankAccounts, useSyncBank, useDisconnectBank, useUpdateBankAccount,
} from '../../hooks/usePlaid';

function AccountsView() {
  const { data: accounts = [] } = useBankAccounts();
  const { data: status } = useBankStatus();
  const updateAccount = useUpdateBankAccount();
  const disconnect = useDisconnectBank();
  const { toast } = useToast();

  const handleDisconnect = async (conn) => {
    if (!window.confirm(
      `Disconnect ${conn.institution_name || 'this bank'}?\n\n` +
      `Its transactions will be removed from the review queue. Expenses you already ` +
      `added stay in the books.`
    )) return;
    try {
      await disconnect.mutateAsync(conn.id);
      toast('Bank disconnected');
    } catch (e) {
      toast(e.message || 'Could not disconnect', 'error');
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, overflow: 'auto' }}>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Account</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>Balance</TableCell>
              <TableCell align="center" sx={{ fontWeight: 600 }}>
                <Tooltip title="Include this account's balance in the cash and runway figures." arrow>
                  <span>In runway</span>
                </Tooltip>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!accounts.length && (
              <TableRow><TableCell colSpan={4} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                No accounts yet.
              </TableCell></TableRow>
            )}
            {accounts.map((a) => (
              <TableRow key={a.id} hover>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {a.name} {a.mask ? `••${a.mask}` : ''}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {a.institution_name}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip size="small" variant="outlined" label={a.subtype || a.type} />
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>{fmtC(a.current_balance)}</TableCell>
                <TableCell align="center">
                  <Switch
                    size="small"
                    checked={!!a.include_in_runway}
                    onChange={(e) => updateAccount.mutate({ id: a.id, includeInRunway: e.target.checked })}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Typography variant="overline" sx={{ color: 'text.secondary' }}>Connections</Typography>
        {(status?.connections || []).map((c) => (
          <Paper key={c.id} sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ flex: 1, minWidth: 200 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {c.institution_name || 'Bank'}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {c.account_count} account{c.account_count === 1 ? '' : 's'}
                {c.last_synced_at ? ` · synced ${new Date(c.last_synced_at).toLocaleString()}` : ' · never synced'}
              </Typography>
            </Box>
            {c.status === 'login_required' ? (
              <>
                <Chip size="small" color="warning" label="Reconnect needed" />
                <ConnectBankButton connectionId={c.id} label="Reconnect" variant="outlined" />
              </>
            ) : c.status === 'error' ? (
              <Tooltip title={c.status_detail || ''} arrow>
                <Chip size="small" color="error" variant="outlined" label="Error" />
              </Tooltip>
            ) : (
              <Chip size="small" color="success" variant="outlined" label="Connected" />
            )}
            <Button
              size="small" color="inherit" startIcon={<LinkOffRoundedIcon sx={{ fontSize: 16 }} />}
              onClick={() => handleDisconnect(c)}
              sx={{ textTransform: 'none', color: 'text.secondary' }}
            >
              Disconnect
            </Button>
          </Paper>
        ))}
        <Box><ConnectBankButton label="Connect another bank" variant="outlined" /></Box>
      </Box>
    </Box>
  );
}

export default function BankPanel() {
  const [view, setView] = useState('review');
  const { data: status, isLoading } = useBankStatus();
  const sync = useSyncBank();
  const { toast } = useToast();

  const handleSync = async () => {
    try {
      const r = await sync.mutateAsync(undefined);
      toast(r.added || r.modified
        ? `${r.added} new, ${r.modified} updated`
        : 'Already up to date');
    } catch (e) {
      toast(e.message || 'Sync failed', 'error');
    }
  };

  if (isLoading) {
    return <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}><CircularProgress size={24} /></Box>;
  }

  if (!status?.configured) {
    return (
      <Alert severity="info" sx={{ mt: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Bank feed not set up yet</Typography>
        <Typography variant="body2">
          {status?.error ? `${status.error}. ` : ''}
          Add <code>PLAID_CLIENT_ID</code> and <code>PLAID_SECRET</code> to{' '}
          <code>server/.env</code> (from dashboard.plaid.com → Team Settings → Keys),
          then restart the backend.
        </Typography>
      </Alert>
    );
  }

  const connections = status.connections || [];

  if (!connections.length) {
    return (
      <Paper sx={{ p: 5, textAlign: 'center', mt: 1 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>Connect your bank</Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3, maxWidth: 460, mx: 'auto' }}>
          Pull checking and credit card activity into the portal to categorize spend,
          attribute charges to jobs, and track runway. Read-only: your bank login is
          entered with Plaid and never touches this server.
        </Typography>
        <ConnectBankButton label="Connect a bank" />
        {status.env !== 'production' && (
          <Typography variant="caption" sx={{ display: 'block', mt: 2, color: 'warning.main' }}>
            Running in {status.env} mode — use Plaid's test credentials, not a real bank login.
          </Typography>
        )}
      </Paper>
    );
  }

  const needsAttention = connections.filter((c) => c.status !== 'active');

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: '100%', minHeight: 0 }}>
      {status.env !== 'production' && (
        <Alert severity="warning" sx={{ py: 0.25 }}>
          Plaid <strong>{status.env}</strong> environment — this is test data, not your real accounts.
        </Alert>
      )}
      {needsAttention.map((c) => (
        <Alert key={c.id} severity="warning" sx={{ py: 0.25 }}
          action={c.status === 'login_required'
            ? <ConnectBankButton connectionId={c.id} label="Reconnect" variant="text" />
            : null}>
          {c.institution_name || 'A bank'}: {c.status_detail || 'needs attention'}
        </Alert>
      ))}

      <RunwayStrip />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <ToggleButtonGroup size="small" exclusive value={view} onChange={(_, v) => v && setView(v)}>
          <ToggleButton value="review" sx={{ textTransform: 'none', px: 2 }}>
            Review
            {!!status.reviewQueueCount && (
              <Chip size="small" label={status.reviewQueueCount} color="primary"
                sx={{ ml: 0.75, height: 18, fontSize: 11 }} />
            )}
          </ToggleButton>
          <ToggleButton value="spend" sx={{ textTransform: 'none', px: 2 }}>Spend</ToggleButton>
          <ToggleButton value="accounts" sx={{ textTransform: 'none', px: 2 }}>Accounts</ToggleButton>
        </ToggleButtonGroup>

        <Tooltip title="Plaid refreshes on its own a few times a day. Use this if you want it now." arrow>
          <span>
            <Button
              size="small" onClick={handleSync} disabled={sync.isPending}
              startIcon={sync.isPending
                ? <CircularProgress size={14} />
                : <RefreshRoundedIcon sx={{ fontSize: 18 }} />}
              sx={{ textTransform: 'none' }}
            >
              {sync.isPending ? 'Syncing…' : 'Sync now'}
            </Button>
          </span>
        </Tooltip>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0 }}>
        {view === 'review' && <ReviewQueue />}
        {view === 'spend' && <SpendByCategory />}
        {view === 'accounts' && <AccountsView />}
      </Box>
    </Box>
  );
}
